// Guided agent identity — compose a bot's `personalidad.identidad` (a prompt
// fragment that tells the model WHO it is) from a few plain questions a
// non-technical owner can answer, instead of asking them to write prompt text.
//
// The structured answers (`IdentityProfile`) are stored on
// `personalidad.identity_profile` so the form round-trips (prefills previous
// answers); `composeIdentity` derives the actual `identidad` string the runtime
// uses. Pure (no deps) so the web editor can preview live AND the API can
// compose authoritatively from the same logic — exported via the
// `@vysto/shared/bot-config/identity` subpath so the web never pulls the barrel.

/** One vertical-specific business fact (label + value), e.g. for a hospital
 *  `{ label: 'Especialidades', value: 'Cardiología, Pediatría…' }`. Self-
 *  describing so the engine renders it without knowing the vertical; the web
 *  form (which IS vertical-aware) decides which labels to collect. */
export interface BusinessField {
  label: string
  value: string
}

/** The plain-language answers an owner gives about their business. The first
 *  block (name/activity/agentName/treatment) composes the agent's IDENTITY; the
 *  rest are the business ESSENTIALS that go ALWAYS into the prompt (never
 *  retrieval-dependent), so the agent always knows where it is, its hours, phone,
 *  services, etc. (the structural fix for "the address was in the KB but the
 *  retrieval missed it"). */
export interface IdentityProfile {
  /** ¿Cómo se llama tu negocio? */
  businessName: string
  /** ¿A qué se dedica? (en pocas palabras) */
  activity: string
  /** ¿Cómo se llama el asistente? (opcional) */
  agentName?: string | undefined
  /** ¿Cómo se dirige al cliente? — de "tú" (cercano) o de "usted" (formal). */
  treatment: 'tu' | 'usted'
  /** Algo más que el asistente deba saber o tener en cuenta (opcional). */
  extra?: string | undefined
  // ─── Business essentials (always in the prompt) ──────────────────────────
  /** Ubicación / dirección. */
  address?: string | undefined
  /** Horario de atención. */
  hours?: string | undefined
  /** Teléfono u otra forma de contacto. */
  phone?: string | undefined
  /** Servicios o productos principales. */
  services?: string | undefined
  /** Cómo agendar una cita o comprar. */
  booking?: string | undefined
  /** Vertical-specific facts (especialidades, marcas, artículos aceptados…). */
  verticalFields?: BusinessField[] | undefined
  /** Aseguradoras con las que el negocio trabaja (para responder de forma
   *  determinista "¿aceptan mi seguro?"). Ej: ['GNP', 'AXA', 'MetLife']. */
  acceptedInsurers?: string[] | undefined
}

/**
 * The bot's `personalidad.tono` DERIVED from the owner's tú/usted answer.
 *
 * Formality has exactly one control: `identity_profile.treatment`, the question
 * the client answers in the Perfil del negocio. `tono` is downstream of it, so
 * the identity sentence (`composeIdentity`) and the generator's "Tono:" line can
 * never disagree inside the same system block.
 *
 * Returns the STORED vocabulary (`Tono` = 'formal' | 'cordial'); the operator
 * console displays 'cordial' as "Cercano". Typed as the literal union rather
 * than importing `Tono` because `types.ts` imports THIS file (cycle).
 */
export function tonoForTreatment(treatment: IdentityProfile['treatment']): 'formal' | 'cordial' {
  return treatment === 'usted' ? 'formal' : 'cordial'
}

/**
 * The DETERMINISTIC line the runtime replies with to a bare greeting ("Hola")
 * when retrieval returned nothing — the one place `tono` is spoken verbatim,
 * with no LLM in between.
 *
 * It lives in shared (not in the engine) because the operator console's "Vista
 * previa" shows this exact line: a preview that renders a hand-written sentence
 * is a painted door, and a preview that copy-pastes the runtime's literal drifts
 * from it on the first edit. One constant, two readers (C3).
 *
 * `suppressGreeting` = the flow already said "Hola" this turn, so the reply
 * skips its own and just nudges.
 */
export function greetingReplyForTono(
  tono: 'formal' | 'cordial',
  opts?: { suppressGreeting?: boolean },
): string {
  const question = tono === 'formal' ? '¿En qué puedo ayudarle?' : '¿En qué puedo ayudarte?'
  return opts?.suppressGreeting ? question : `¡Hola! ${question}`
}

/**
 * The SALUTATION half of the greeting reply — "¡Hola! " — with the question
 * dropped (R2b).
 *
 * Derived from `greetingReplyForTono` by subtraction rather than written out
 * again, because a second literal here would be a THIRD place `tono` is spoken
 * and the three would drift. Whatever register the greeting reply speaks, this
 * speaks the same one, including any future divergence between tú and usted.
 *
 * Used by the Skill walker when a confident route suppressed the opening
 * greeting and the turn's first bubble turned out to be a QUESTION: the
 * salutation is prefixed to it so the conversation still opens with a hello,
 * in ONE bubble instead of two.
 */
export function salutationForTono(tono: 'formal' | 'cordial'): string {
  const full = greetingReplyForTono(tono)
  const question = greetingReplyForTono(tono, { suppressGreeting: true })
  return full.endsWith(question) ? full.slice(0, full.length - question.length) : ''
}

/**
 * What the agent answers to a PHATIC message mid-conversation — a "gracias",
 * an "ok", a "listo" that carries no question and no request (R3).
 *
 * It exists because the alternative the runtime used to reach for was the
 * knowledge base, and a knowledge base has nothing to say to "gracias": the
 * customer got "Por ahora no tengo esa información para responderte con
 * certeza. ¿Quieres que te comunique con una persona…?" for saying thank you.
 * The honest reply to a pleasantry is a pleasantry, so it is deterministic
 * copy, not a generation — it can never invent a fact.
 *
 * Lives next to `greetingReplyForTono` because it is the same kind of line: the
 * bot speaking in its own register with no LLM in between.
 */
export function phaticAckForTono(tono: 'formal' | 'cordial'): string {
  return tono === 'formal'
    ? 'Con gusto. Aquí sigo si necesita algo más.'
    : 'Con gusto. Aquí sigo si necesitas algo más.'
}

/** Whether a profile has the minimum to compose a usable identity. */
export function isIdentityProfileComplete(p: IdentityProfile): boolean {
  return p.businessName.trim().length > 0 && p.activity.trim().length > 0
}

/** Compose the always-in-prompt "Datos del negocio" block from the business
 *  essentials (everything except the identity sentence, which `composeIdentity`
 *  handles). Returns '' when nothing factual is set. Only includes lines for
 *  fields the owner actually filled in. */
export function composeBusinessFacts(p: IdentityProfile): string {
  const lines: string[] = []
  const add = (label: string, value: string | undefined): void => {
    const v = value?.trim()
    if (v) lines.push(`- ${label}: ${v}`)
  }
  add('Dirección', p.address)
  add('Horario', p.hours)
  add('Teléfono / contacto', p.phone)
  add('Servicios o productos', p.services)
  add('Cómo agendar o comprar', p.booking)
  for (const f of p.verticalFields ?? []) add(f.label.trim(), f.value)
  const insurers = (p.acceptedInsurers ?? []).map((s) => s.trim()).filter(Boolean)
  if (insurers.length > 0) add('Aseguradoras aceptadas', insurers.join(', '))
  add('Más información', p.extra)
  if (lines.length === 0) return ''
  return `Datos del negocio (usa esta información para responder con certeza):\n${lines.join('\n')}`
}

/** Compose the `personalidad.identidad` prompt fragment from the owner's
 *  answers. Kept concise (fits the config field) and reinforces the
 *  defer-instead-of-improvise behavior in the agent's own self-description. */
export function composeIdentity(p: IdentityProfile): string {
  const name = p.agentName?.trim() ? p.agentName.trim() : 'el asistente'
  const business = p.businessName.trim()
  const activity = p.activity.trim()
  const trato =
    p.treatment === 'usted'
      ? 'Te diriges de usted, de forma formal y profesional.'
      : 'Hablas de tú, de forma cálida y cercana.'
  const core = activity
    ? `Eres ${name} de ${business}, ${activity}.`
    : `Eres ${name} de ${business}.`
  // `extra` + the business facts now live in the always-in-prompt "Datos del
  // negocio" block (composeBusinessFacts), not in this identity sentence.
  return `${core} ${trato} Respondes con la información del negocio; si no la tienes, ofreces pasar con una persona y no inventas.`
}
